import type { FlightSearchParams } from 'hooks/useSearchFlights'
import { buildFlightContext } from 'tripPlanner/components/Chat/Flights/buildFlightContext'
import {
  isLeFlightSavedItemData,
  type TransportOption,
} from 'tripPlanner/api/types'
import type {
  BookedShortlistedFlightDetailsSection,
  ShortlistedFlightDetailsSection,
} from '../../components/ShortlistedFlightsSection/types'
import { compactLines } from './message'

const MAX_FLIGHT_RESULTS_IN_CONTEXT = 5

interface BuildFlightSearchDrawerContextArgs {
  intentParams: FlightSearchParams
  searchParams?: FlightSearchParams
  hasSearched: boolean
  fetching: boolean
  hasError: boolean
  activeSegmentLabel: string
  sort: string
  activeFilterCount: number
  displayJourneys: Array<App.JourneyV2>
  visibleJourneys: Array<App.JourneyV2>
  selectedFlights: Record<string, App.JourneyV2>
  searchId?: string
  sessionId?: string
  searchExpiry?: number
}

interface BuildFlightDetailDrawerContextArgs {
  title: string
  option: TransportOption
  sections: Array<ShortlistedFlightDetailsSection>
  price?: number
  currencyCode?: string
  canBookNow: boolean
}

interface BuildBookedFlightDetailDrawerContextArgs {
  title: string
  option: TransportOption
  sections: Array<BookedShortlistedFlightDetailsSection>
  bookingUrl?: string
}

interface FlightItineraryLeg {
  departureAirport?: string
  arrivalAirport?: string
  departureDate?: string
}

function formatFlightItinerary(itinerary: Array<FlightItineraryLeg>): string {
  if (!itinerary.length) return 'No complete itinerary selected'
  return itinerary
    .map((leg, index) => {
      const route = `${leg.departureAirport || '?'} -> ${leg.arrivalAirport || '?'}`
      const date = leg.departureDate ? ` on ${leg.departureDate}` : ''
      return `Leg ${index + 1}: ${route}${date}`
    })
    .join('; ')
}

function formatFlightSearchParams(params: FlightSearchParams): string {
  return compactLines([
    `Itinerary: ${formatFlightItinerary(params.itinerary)}`,
    `Fare type: ${params.fareType ?? 'oneWay'}`,
    `Fare class: ${params.fareClass ?? 'economy'}`,
    `Occupancies: ${JSON.stringify(params.occupancies)}`,
  ])
}

function formatSelectedFlights(
  selectedFlights: Record<string, App.JourneyV2>,
): string | undefined {
  const entries = Object.entries(selectedFlights)
  if (!entries.length) return undefined

  return entries
    .map(([segment, journey]) => `${segment}:\n${buildFlightContext(journey)}`)
    .join('\n\n')
}

function formatFlightResultSummaries(journeys: Array<App.JourneyV2>): string {
  if (!journeys.length) return 'No visible flight results'

  return journeys
    .slice(0, MAX_FLIGHT_RESULTS_IN_CONTEXT)
    .map((journey, index) => `Result ${index + 1}:\n${buildFlightContext(journey)}`)
    .join('\n\n')
}

function formatLeFlightSavedData(option: TransportOption): string | undefined {
  const saved = isLeFlightSavedItemData(option.savedItemData)
    ? option.savedItemData
    : undefined
  if (!saved) return undefined

  return compactLines([
    `Saved route: ${saved.departureAirport ?? '?'} -> ${saved.arrivalAirport ?? '?'}`,
    saved.departureDate && `Departure date: ${saved.departureDate}`,
    saved.departureTime && `Departure time: ${saved.departureTime}`,
    saved.arrivalDate && `Arrival date: ${saved.arrivalDate}`,
    saved.arrivalTime && `Arrival time: ${saved.arrivalTime}`,
    saved.airline && `Airline: ${saved.airline}`,
    saved.flightNumber && `Flight number: ${saved.flightNumber}`,
    saved.fareCurrencyCode && saved.farePrice !== undefined
    && `Fare: ${saved.fareCurrencyCode} ${saved.farePrice}`,
    saved.orderId && `Order ID: ${saved.orderId}`,
  ])
}

function formatCartFlight(section: Extract<
  ShortlistedFlightDetailsSection,
  { cartFlight: App.Checkout.FlightItemFlight }
>): string {
  const flight = section.cartFlight
  return compactLines([
    `Cart flight: ${flight.departingAirportCode ?? '?'} -> ${flight.arrivalAirportCode ?? '?'}`,
    flight.departureDateTime && `Departure: ${flight.departureDateTime}`,
    flight.arrivalDateTime && `Arrival: ${flight.arrivalDateTime}`,
    flight.duration && `Duration: ${flight.duration.hours}h ${flight.duration.minutes}m`,
    section.isClassicReward !== undefined
    && `Classic reward: ${section.isClassicReward}`,
  ])
}

function formatBookedLeg(section: BookedShortlistedFlightDetailsSection): string {
  const leg = section.leg
  return compactLines([
    `Booked leg: ${leg.departureAirport ?? '?'} -> ${leg.arrivalAirport ?? '?'}`,
    leg.departureDateTime && `Departure: ${leg.departureDateTime}`,
    leg.arrivalDateTime && `Arrival: ${leg.arrivalDateTime}`,
    section.isDeparting !== undefined && `Departing leg: ${section.isDeparting}`,
    section.isStandalone !== undefined && `Standalone: ${section.isStandalone}`,
  ])
}

export function buildFlightSearchDrawerContext({
  intentParams,
  searchParams,
  hasSearched,
  fetching,
  hasError,
  activeSegmentLabel,
  sort,
  activeFilterCount,
  displayJourneys,
  visibleJourneys,
  selectedFlights,
  searchId,
  sessionId,
  searchExpiry,
}: BuildFlightSearchDrawerContextArgs): string {
  const selectedFlightSummary = formatSelectedFlights(selectedFlights)
  const visibleResultsSummary = hasSearched
    ? formatFlightResultSummaries(visibleJourneys)
    : undefined

  return compactLines([
    'Drawer: Flight search',
    `Status: ${fetching ? 'searching' : hasError ? 'error' : hasSearched ? 'searched' : 'not searched'}`,
    `Current search intent:\n${formatFlightSearchParams(intentParams)}`,
    searchParams && `Submitted search:\n${formatFlightSearchParams(searchParams)}`,
    hasSearched && `Active segment: ${activeSegmentLabel}`,
    hasSearched && `Sort: ${sort}`,
    hasSearched && `Active filter count: ${activeFilterCount}`,
    hasSearched && `Results before filters: ${displayJourneys.length}`,
    hasSearched && `Visible results: ${visibleJourneys.length}`,
    selectedFlightSummary && `Selected legs:\n${selectedFlightSummary}`,
    visibleResultsSummary && `Top visible results:\n${visibleResultsSummary}`,
    searchId && `Search ID: ${searchId}`,
    sessionId && `Session ID: ${sessionId}`,
    searchExpiry ? `Search expiry: ${searchExpiry}` : undefined,
  ])
}

export function buildFlightDetailDrawerContext({
  title,
  option,
  sections,
  price,
  currencyCode,
  canBookNow,
}: BuildFlightDetailDrawerContextArgs): string {
  const sectionSummaries = sections.map((section, index) => {
    if ('journey' in section) {
      return `Section ${index + 1}:\n${buildFlightContext(section.journey)}`
    }
    return `Section ${index + 1}:\n${formatCartFlight(section)}`
  })

  return compactLines([
    'Drawer: Shortlisted flight details',
    `Title: ${title}`,
    option.label && `Option label: ${option.label}`,
    formatLeFlightSavedData(option),
    price !== undefined && currencyCode && `Price: ${currencyCode} ${price}`,
    `Book now available: ${canBookNow}`,
    `Sections: ${sections.length}`,
    sectionSummaries.join('\n\n'),
  ])
}

export function buildBookedFlightDetailDrawerContext({
  title,
  option,
  sections,
  bookingUrl,
}: BuildBookedFlightDetailDrawerContextArgs): string {
  return compactLines([
    'Drawer: Booked shortlisted flight details',
    `Title: ${title}`,
    option.label && `Option label: ${option.label}`,
    formatLeFlightSavedData(option),
    bookingUrl && `Booking URL: ${bookingUrl}`,
    `Sections: ${sections.length}`,
    sections
      .map((section, index) => `Section ${index + 1}:\n${formatBookedLeg(section)}`)
      .join('\n\n'),
  ])
}
